import {ZIDX, ZIDXPage} from "../../../standalone/standaloneLibrary.js";

const API_URL = "/account/data-feeds/mlsgridImageDebug/api";
const POLL_MILLISECONDS = 2000;
const GIVE_UP_MILLISECONDS = 10 * 60 * 1000;

/**
 * Superadmin "Debug Image Download" page for MLS Grid feeds. Asks the server to queue one listing for the image
 * worker (data process) and polls the queue state, image states and step log until the worker is finished.
 * Uploaded images have links to the CDN files, and once the job is finished the server HEAD-checks them.
 */
export class ZIDXAccountDataFeedMlsGridImageDebug implements ZIDXPage {
    zidx: ZIDX;
    private pollTimer: number | null = null;
    private runToken = 0;

    constructor(zidx: ZIDX) {
        this.zidx = zidx;
    }

    getPageClass() {
        return "zidxAccountDataFeedMlsGridImageDebugContainer";
    }

    render() {
        const root = this.zidx.peakidxShadowRoot;
        const feedInput = root.getElementById("mlsGridDebugFeedId") as HTMLInputElement | null;
        const listingInput = root.getElementById("mlsGridDebugListingId") as HTMLInputElement | null;
        const ignoreCooldown = root.getElementById("mlsGridDebugIgnoreCooldown") as HTMLInputElement | null;
        const runButton = root.getElementById("mlsGridDebugRun") as HTMLButtonElement | null;
        const statusEl = root.getElementById("mlsGridDebugStatus") as HTMLElement | null;
        const imagesEl = root.getElementById("mlsGridDebugImages") as HTMLElement | null;
        const logEl = root.getElementById("mlsGridDebugLog") as HTMLElement | null;
        if (!feedInput || !listingInput || !ignoreCooldown || !runButton || !statusEl || !imagesEl || !logEl) return;

        const self = this;
        const run = function () {
            if (runButton.disabled) return;
            const listingId = listingInput.value.trim();
            if (!listingId) {
                statusEl.textContent = "Enter a listing ID.";
                return;
            }
            self.stopPolling();
            const token = ++self.runToken;
            runButton.disabled = true;
            statusEl.textContent = "Requesting...";
            imagesEl.innerHTML = "";
            logEl.textContent = "";
            self.zidx.ajaxRequest({
                id: "mlsGridImageDebugRun",
                method: "post",
                postObj: {
                    action: "run",
                    listing_feed_id: feedInput.value,
                    listing_id: listingId,
                    ignore_cooldown: ignoreCooldown.checked ? "1" : "0"
                },
                url: API_URL,
                callback: function (r: string) {
                    let js: any;
                    try {
                        js = JSON.parse(r);
                    } catch (e) {
                        runButton.disabled = false;
                        statusEl.textContent = "Could not read the response.";
                        return;
                    }
                    if (!js.success) {
                        runButton.disabled = false;
                        statusEl.textContent = "Error: " + (js.errorMessage || "Request failed.");
                        return;
                    }
                    statusEl.textContent = js.message || "Queued.";
                    self.poll(token, feedInput.value, js.queueId, 0, Date.now(), statusEl, imagesEl, logEl, runButton);
                },
                errorCallback: function () {
                    runButton.disabled = false;
                    statusEl.textContent = "Network error. Please try again.";
                }
            });
        };
        runButton.addEventListener("click", run);
        listingInput.addEventListener("keydown", function (e: KeyboardEvent) {
            if (e.key === "Enter") run();
        });
    }

    private stopPolling() {
        if (this.pollTimer !== null) {
            window.clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private poll(token: number, feedId: string, queueId: number, afterId: number, startedAt: number,
                 statusEl: HTMLElement, imagesEl: HTMLElement, logEl: HTMLElement, runButton: HTMLButtonElement) {
        const self = this;
        this.zidx.ajaxRequest({
            id: "mlsGridImageDebugStatus",
            method: "post",
            postObj: {action: "status", listing_feed_id: feedId, queue_id: String(queueId), after_id: String(afterId)},
            url: API_URL,
            callback: function (r: string) {
                if (token !== self.runToken) return; // a newer run replaced this one
                let js: any;
                try {
                    js = JSON.parse(r);
                } catch (e) {
                    runButton.disabled = false;
                    statusEl.textContent = "Could not read the status response.";
                    return;
                }
                if (!js.success) {
                    runButton.disabled = false;
                    statusEl.textContent = "Error: " + (js.errorMessage || "Status failed.");
                    return;
                }
                let nextAfter = afterId;
                for (const line of js.logs || []) {
                    self.appendLog(logEl, "[" + line.time + " UTC] " + line.message);
                    nextAfter = Math.max(nextAfter, line.id);
                }
                logEl.scrollTop = logEl.scrollHeight;
                self.renderImages(imagesEl, js.images || []);
                const queue = js.queue || {};
                statusEl.textContent = self.describeQueue(queue, js.done);
                if (js.done) {
                    runButton.disabled = false;
                    if (queue.lastError) {
                        self.appendLog(logEl, "Last error stored on the queue entry: " + queue.lastError);
                    }
                    // one more request that also checks the uploaded files on the CDN
                    self.checkImages(feedId, queueId, nextAfter, imagesEl, logEl);
                    return;
                }
                if (Date.now() - startedAt > GIVE_UP_MILLISECONDS) {
                    runButton.disabled = false;
                    statusEl.textContent += " Stopped waiting after 10 minutes. The worker may be busy, stopped or the listing may be scheduled for later.";
                    return;
                }
                self.pollTimer = window.setTimeout(function () {
                    self.poll(token, feedId, queueId, nextAfter, startedAt, statusEl, imagesEl, logEl, runButton);
                }, POLL_MILLISECONDS);
            },
            errorCallback: function () {
                if (token !== self.runToken) return;
                self.pollTimer = window.setTimeout(function () {
                    self.poll(token, feedId, queueId, afterId, startedAt, statusEl, imagesEl, logEl, runButton);
                }, POLL_MILLISECONDS * 2);
            }
        });
    }

    /** Adds a log line; http(s) URLs in it become links that open in a new tab. */
    private appendLog(logEl: HTMLElement, text: string) {
        const parts = text.split(/(https?:\/\/\S+)/);
        for (const part of parts) {
            if (/^https?:\/\//.test(part)) {
                logEl.appendChild(this.link(part, part));
            } else if (part) {
                logEl.appendChild(document.createTextNode(part));
            }
        }
        logEl.appendChild(document.createTextNode("\n"));
    }

    /** After the job finished, ask the server to HEAD-check the uploaded files on the CDN and show the answers. */
    private checkImages(feedId: string, queueId: number, afterId: number, imagesEl: HTMLElement, logEl: HTMLElement) {
        const self = this;
        this.zidx.ajaxRequest({
            id: "mlsGridImageDebugCheck",
            method: "post",
            postObj: {action: "status", listing_feed_id: feedId, queue_id: String(queueId), after_id: String(afterId), check_images: "1"},
            url: API_URL,
            callback: function (r: string) {
                try {
                    const js = JSON.parse(r);
                    if (!js.success) return;
                    self.renderImages(imagesEl, js.images || []);
                    for (const image of js.images || []) {
                        if (!image.webpHttp) continue;
                        self.appendLog(logEl, "CDN check, image " + image.order + ": jpg " + self.httpText(image.jpgHttp)
                            + ", webp " + self.httpText(image.webpHttp) + ", thumbnail " + self.httpText(image.thumbnailHttp));
                    }
                    logEl.scrollTop = logEl.scrollHeight;
                } catch (e) {
                    // the check is optional
                }
            }
        });
    }

    private httpText(code: number): string {
        if (!code) return "not checked";
        if (code < 0) return "request failed";
        return "HTTP " + code + (code === 200 ? " ok" : " NOT OK");
    }

    private describeQueue(queue: any, done: boolean): string {
        let text = "Queue: " + (queue.status || "?") + ", phase " + (queue.phase || "?") + ", attempts " + (queue.attemptCount || 0) + ".";
        if (queue.processing) {
            text += " The image worker is processing this listing now.";
        } else if (!done) {
            if (queue.secondsUntilNextAttempt > 5) {
                text += " Waiting for the source cooldown, next attempt in " + this.formatSeconds(queue.secondsUntilNextAttempt) + ".";
            } else {
                text += " Waiting for the image worker to pick it up.";
            }
        } else {
            text += " Finished.";
        }
        return text;
    }

    private formatSeconds(seconds: number): string {
        return seconds >= 120 ? Math.round(seconds / 60) + " minutes" : seconds + " seconds";
    }

    private renderImages(container: HTMLElement, images: any[]) {
        container.innerHTML = "";
        if (images.length === 0) return;
        const table = document.createElement("table");
        table.style.borderCollapse = "collapse";
        table.style.margin = "8px 0";
        const header = document.createElement("tr");
        for (const title of ["#", "Image ID", "Status", "Preview", "Files on the CDN"]) {
            const th = document.createElement("th");
            th.textContent = title;
            th.style.padding = "4px 10px";
            th.style.textAlign = "left";
            header.appendChild(th);
        }
        table.appendChild(header);
        for (const image of images) {
            const tr = document.createElement("tr");
            const cells: HTMLElement[] = [];
            for (let i = 0; i < 5; i++) {
                const td = document.createElement("td");
                td.style.padding = "4px 10px";
                td.style.verticalAlign = "middle";
                cells.push(td);
                tr.appendChild(td);
            }
            cells[0].textContent = String(image.order);
            // the image ID opens the full image once the file is on the CDN
            if (image.webpUrl) {
                cells[1].appendChild(this.link(String(image.imageId), image.webpUrl));
            } else {
                cells[1].textContent = String(image.imageId);
            }
            cells[2].textContent = image.statusText;
            if (image.thumbnailUrl) {
                const previewLink = this.link("", image.webpUrl);
                const img = document.createElement("img");
                img.src = image.thumbnailUrl;
                img.alt = "Image " + image.order;
                img.loading = "lazy";
                img.style.height = "48px";
                img.style.border = "1px solid rgba(128,128,128,.4)";
                previewLink.appendChild(img);
                cells[3].appendChild(previewLink);
                const files: [string, string, number][] = [
                    ["JPG", image.jpgUrl, image.jpgHttp],
                    ["WebP", image.webpUrl, image.webpHttp],
                    ["Thumbnail", image.thumbnailUrl, image.thumbnailHttp]
                ];
                files.forEach(([label, url, code], n) => {
                    if (n > 0) cells[4].appendChild(document.createTextNode("  "));
                    cells[4].appendChild(this.link(label, url));
                    if (code) {
                        const state = document.createElement("span");
                        state.textContent = " (" + this.httpText(code) + ")";
                        state.style.color = code === 200 ? "green" : "#c00";
                        cells[4].appendChild(state);
                    }
                });
            }
            table.appendChild(tr);
        }
        container.appendChild(table);
    }

    private link(label: string, url: string): HTMLAnchorElement {
        const a = document.createElement("a");
        a.textContent = label;
        if (/^https?:\/\//i.test(url)) a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        return a;
    }
}
